import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../../../components/common/detail'
import { FormField, FormGrid, FormNote, FormSection } from '../../../components/common/form'
import { TypeAction, TypeActionTranslations, status as STATUS_VALUES } from '../../../utils/constants'
import NumberInput from '../../../components/common/NumberInput'
import ContextFields from './ContextFields'
import { SlideOver } from './RowEditPanel'
import { EMPTY_CONTEXT, ImportContext, ImportRow } from './importRowModel'

type BulkEditPanelProps = {
  count: number
  onApply: (patch: Partial<ImportRow>) => void
  onClose: () => void
  onLabels: (labels: Record<string, string>) => void
  lockMission?: boolean
  lockProject?: boolean
  preset?: ImportContext
}

type PlanningDraft = {
  DateDebut: string
  DateFin: string
  Cout: string
  Priority: string
  TypeAction: string
  Status: string
}

const EMPTY_PLANNING: PlanningDraft = {
  DateDebut: '',
  DateFin: '',
  Cout: '',
  Priority: '',
  TypeAction: '',
  Status: '',
}

/** Empty field = keep each row's current value. */
export function buildBulkPatch(
  planning: PlanningDraft,
  context: ImportContext,
  locks?: { mission?: boolean; project?: boolean }
): Partial<ImportRow> {
  const patch: Partial<ImportRow> = {}
  if (planning.DateDebut) patch.DateDebut = planning.DateDebut
  if (planning.DateFin) patch.DateFin = planning.DateFin
  if (planning.Cout !== '') patch.Cout = planning.Cout
  if (planning.Priority) patch.Priority = Number(planning.Priority)
  if (planning.TypeAction) patch.TypeAction = planning.TypeAction
  if (planning.Status) patch.Status = planning.Status
  if (context.MissionId) {
    patch.MissionId = context.MissionId
    if (locks?.project || context.ProjetId || !locks?.mission) {
      patch.ProjetId = context.ProjetId
    }
    const sourcesChosen =
      context.SourceAudit.length +
        context.SourceIncident.length +
        context.SourceRisque.length >
      0
    if (!locks?.mission || sourcesChosen) {
      patch.SourceAudit = context.SourceAudit
      patch.SourceIncident = context.SourceIncident
      patch.SourceRisque = context.SourceRisque
    }
  }
  if (context.SecurityIds.length) {
    patch.SecurityIds = context.SecurityIds
    patch.SecurityControlIds = context.SecurityControlIds
    patch.SubDomainIds = context.SubDomainIds
  }
  return patch
}

export default function BulkEditPanel({
  count,
  onApply,
  onClose,
  onLabels,
  lockMission = false,
  lockProject = false,
  preset,
}: BulkEditPanelProps) {
  const { t } = useTranslation()
  const [planning, setPlanning] = useState<PlanningDraft>(EMPTY_PLANNING)
  const [context, setContext] = useState<ImportContext>(preset ?? EMPTY_CONTEXT)

  const patch = buildBulkPatch(planning, context, { mission: lockMission, project: lockProject })
  const changedCount = Object.keys(patch).length
  const keep = t('actionImport.bulk.keep')

  const set = (p: Partial<PlanningDraft>) => setPlanning((d) => ({ ...d, ...p }))

  return (
    <SlideOver
      title={t('actionImport.bulk.title', { count })}
      subtitle={t('actionImport.bulk.subtitle')}
      onClose={onClose}
      footer={
        <>
          <DetailButton variant="ghost" onClick={onClose}>
            {t('buttons.cancel', { defaultValue: 'Cancel' })}
          </DetailButton>
          <DetailButton onClick={() => onApply(patch)} disabled={!changedCount}>
            {t('actionImport.bulk.apply', { count })}
          </DetailButton>
        </>
      }
    >
      <FormNote>{t('actionImport.bulk.note')}</FormNote>

      <FormSection title={t('actionCreate.sections.planning.title')}>
        <FormGrid cols={2}>
          <FormField label={t('actionImport.fields.startDate')}>
            <input
              className="incyia-form-inp"
              type="date"
              value={planning.DateDebut}
              onChange={(e) => set({ DateDebut: e.target.value })}
            />
          </FormField>
          <FormField label={t('actionImport.fields.endDate')}>
            <input
              className="incyia-form-inp"
              type="date"
              value={planning.DateFin}
              onChange={(e) => set({ DateFin: e.target.value })}
            />
          </FormField>
          <FormField label={t('actionCreate.sections.planning.cost')}>
            <NumberInput
              currency
              min={0}
              placeholder={keep}
              value={planning.Cout}
              onChange={(v) => set({ Cout: v === null ? '' : String(v) })}
            />
          </FormField>
          <FormField label={t('actionCreate.sections.planning.priority')}>
            <select
              className="incyia-form-inp"
              value={planning.Priority}
              onChange={(e) => set({ Priority: e.target.value })}
            >
              <option value="">{keep}</option>
              {[1, 2, 3, 4].map((p) => (
                <option key={p} value={p}>
                  {t(`priority.${p}`)}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={t('actionCreate.sections.planning.type')}>
            <select
              className="incyia-form-inp"
              value={planning.TypeAction}
              onChange={(e) => set({ TypeAction: e.target.value })}
            >
              <option value="">{keep}</option>
              {TypeAction.map((type) => (
                <option key={type} value={type}>
                  {t(TypeActionTranslations[type])}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={t('actionCreate.sections.planning.status')}>
            <select
              className="incyia-form-inp"
              value={planning.Status}
              onChange={(e) => set({ Status: e.target.value })}
            >
              <option value="">{keep}</option>
              {STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {t(s)}
                </option>
              ))}
            </select>
          </FormField>
        </FormGrid>
      </FormSection>

      <ContextFields
        value={context}
        onChange={setContext}
        onLabels={onLabels}
        cols={2}
        lockMission={lockMission}
        lockProject={lockProject}
      />
    </SlideOver>
  )
}
